import {AfterViewInit, Component, EventEmitter, forwardRef, Input, OnInit, Output, TemplateRef} from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { BusinessNumberSearchFn, BusinessSearchFn, getEntityType, } from '@portal-workspace/grow-ui-library';
import { PersonalAssetComponentEvent, PersonalAssetsComponent } from './personal-assets.component';
import { PersonalLiabilitiesComponentEvent, PersonalLiabilitiesComponent } from './personal-liabilities.component';
import {
  createAbnInputMask,
  createAcnInputMask,
  createEmailInputMask,
  createPhoneNumberInputMask,
} from '@portal-workspace/grow-ui-library';
import {
  setupUntilDestroy,
  getAddress2ComponentValueFormControlValueFn,
  formControlErrorKeys,
  formControlErrorMessage,
} from '@portal-workspace/grow-ui-library';
import { AbstractControl, NG_VALUE_ACCESSOR, ValidationErrors, ValidatorFn, Validators, FormBuilder, FormGroup, FormArray, FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import {Subject, Subscription} from 'rxjs';
import {AbstractControlValueAccessor} from '../abstract-control-value-accessor';
import {delay, distinctUntilChanged, tap} from 'rxjs/operators';
import {
  BusinessNumberSearchValue,
  compareMatch,
  EntityTypes,
  EntityTypeValue,
  fromIndividualFormValue,
  IndividualFormValue,
  IndividualOrEntity,
  KYB_MIN_PARTNERS,
  KybManualPerson,
  KybIndividualTrusteeDialogData,
  KybPerson,
  KybPersonRoleSelection,
  KybResult,
  KybSignerEntityView,
  kybDisplayStatus,
  PlacesSearchResultFn,
  PlacesDetailsFn,
  toIndividualFormValue
} from '@portal-workspace/grow-shared-library';
import {UntilDestroy} from '@ngneat/until-destroy';
import {MARK, Mark} from '@portal-workspace/grow-ui-library/mark';
import {EntityPartner, IndividualPartner, PartnerValue } from '@portal-workspace/grow-shared-library';
import { MatFormFieldModule } from '@angular/material/form-field';
import { EntityTypeComponent } from '../entity-type-component/entity-type.component';
import { BusinessNumberSearchComponent } from '../business-number-search-component/business-number-search.component';
import { MatButtonModule } from '@angular/material/button';
import { MarkDirective } from '../../directives/mark-as-dirty.directive';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { AuthService } from 'apps/portal2/src/app/service/auth.service';
import { ApplicationDialogService } from '../application-dialog-component/application-dialog.service';
import { IndividualFormComponent } from '../individual-form-component/individual-form.component';
import { KybEntitySectionComponent } from '../kyb-key-people-component/kyb-entity-section.component';
import { KybStatusComponent } from '../kyb-key-people-component/kyb-status.component';
import { MessageBoxComponent } from '../message-box/message-box.component';
import { ManualKybPeopleComponent } from '../kyb-key-people-component/manual-kyb-people.component';


export interface PartnerComponentEvent {
  valid: boolean,
  entries: (IndividualPartner | EntityPartner)[];
}

@UntilDestroy({arrayName: 'subscriptions'})
@Component({
    selector: 'partner',
    templateUrl: './partner.component.html',
    styleUrls: ['./partner.component.scss'],
    providers: [
        { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => PartnerComponent), multi: true },
        { provide: MARK, useExisting: forwardRef(() => PartnerComponent) },
    ],
    standalone: true,
    imports: [NgTemplateOutlet, MatButtonToggleModule, MatCheckboxModule, FormsModule, ReactiveFormsModule, MarkDirective, IndividualFormComponent, MatButtonModule, PersonalAssetsComponent, PersonalLiabilitiesComponent, BusinessNumberSearchComponent, EntityTypeComponent, MatFormFieldModule, KybEntitySectionComponent, KybStatusComponent, MessageBoxComponent, ManualKybPeopleComponent]
})
export class PartnerComponent extends AbstractControlValueAccessor<PartnerValue> implements OnInit, AfterViewInit, Mark {

  errorKeys = formControlErrorKeys;
  errorMessage = formControlErrorMessage;
  markObservable: Subject<boolean> = new Subject<boolean>();
  placesSearchResultFn: PlacesSearchResultFn | null = null;
  placesDetailsFn: PlacesDetailsFn | null = null;


  getAddress2ComponentValueFormControlValueFn = getAddress2ComponentValueFormControlValueFn;

  formGroup: FormGroup;
  formArray: FormArray;

  subscription?: Subscription;
  subscriptions: Subscription[] = [];

  @Input({required: true}) businessNumberSearchFn!: BusinessNumberSearchFn;
  @Input({required: false}) showAssetsAndLiabilities = true;
  @Input({required: false}) showAddressForm = true;
  @Input({required: false}) showIdentification = false;
  // KYB "Key People" screen only captures name + dob for individual partners; the full
  // detail (address/guarantor/contact) is collected on a later page.
  @Input({required: false}) kybMode = false;
  // KYB only: candidate individuals shown in the per-entity-partner "Select signers" table. Resolved
  // per row by the container, so each corporate partner shows its OWN officers rather than the parent
  // partnership's stakeholders. Returns null until the row has a usable ABN/ACN; the view carries the
  // run's display status so an in-flight check is not shown as one that returned nobody.
  @Input({required: false}) signerEntityFor: ((row: FormGroup) => KybSignerEntityView | null) | null = null;
  // KYB only: handed to the signers table; see kyb-entity-section.
  @Input({required: false}) guarantorLockedStakeholderIds: ReadonlySet<number> = new Set<number>();
  // A corporate partner is a company, so its signers table follows the company selection rules
  // (e.g. shareholder >= 25% preselected) via the shared kyb-entity-section logic.
  readonly signerEntityType: EntityTypes = 'company';
  // KYB only: extra content rendered inside an entity partner's card, receiving that partner's row
  // as $implicit. Used by the container to nest a trust partner's trustee entry under the partner
  // it belongs to instead of leaving it below the whole partner list.
  @Input({required: false}) nestedPartyTemplate: TemplateRef<{ $implicit: FormGroup }> | null = null;
  // Key People only. Additional Guarantors leaves this off so an empty completed table stays read-only.
  @Input({required: false}) allowManualEntry = false;
  @Output() events: EventEmitter<PartnerComponentEvent>;
  // KYB only: bubble Guarantor/Signer tick changes up to the kyb-key-people container.
  @Output() personRoleChange: EventEmitter<KybPersonRoleSelection> = new EventEmitter<KybPersonRoleSelection>();
  @Output() personSelectedChange: EventEmitter<{ person: KybPerson; selected: boolean }> = new EventEmitter<{ person: KybPerson; selected: boolean }>();
  // KYB only: a corporate partner's own KYB check finished inside its "Select signers" panel, which
  // hands back the whole refreshed read model for the container to apply.
  @Output() signerResolved: EventEmitter<KybResult> = new EventEmitter<KybResult>();
  // KYB only: Retry pressed while a corporate partner's entity is not in the read model yet, so the
  // container is the only one that can re-read it.
  @Output() signerRetry: EventEmitter<void> = new EventEmitter<void>();
  @Output() manualKybEntry = new EventEmitter<number>();
  @Output() manualPeopleChange = new EventEmitter<{ commercialEntityId: number; people: KybManualPerson[] }>();
  @Output() manualValidityChange = new EventEmitter<{ commercialEntityId: number; valid: boolean }>();

  // Entity partners whose "Select signers" table is currently expanded, tracked per form group.
  private signersVisible: Set<FormGroup> = new Set<FormGroup>();

  // A write builds one row per partner it was given, and each row would otherwise announce a list
  // that is still being assembled. Consumers read an emitted list as the complete set of partners,
  // so a half-built one reads as "the partners not added yet were removed".
  private seeding = false;

  disableControls:boolean = true;
  entityTypeReadonly: boolean = true;

  constructor(private formBuilder: FormBuilder, private authService: AuthService, private dialogService: ApplicationDialogService) {
    super();
    this.events = new EventEmitter<PartnerComponentEvent>();
    this.formBuilder = formBuilder;
    this.formArray = formBuilder.array([]);
    this.formGroup = formBuilder.group({
      members: this.formArray,
    });

    
    this.placesSearchResultFn = this.authService.getGooglePlacesSearchResultFn;
    this.placesDetailsFn = this.authService.getGooglePlacesDetailsFn;
  }


  ngOnInit(): void {
    setupUntilDestroy(this);
    this.subscription = this.formArray.valueChanges.pipe(
      delay(0),
      distinctUntilChanged(compareMatch),
      tap(array => {
        const entries = this.formArrayToPartnerValue();
        if (this.formArray.invalid) {
          this.propagateChange(null);
        } else {
          this.propagateChange(entries);
        }
        this.events.emit({valid: this.formArray.valid, entries});
      })
    ).subscribe();

    // KYB only, so legacy partner flows on other pages keep their current (unenforced) behaviour.
    if (this.kybMode) {
      this.formArray.setValidators([this.minimumPartnersValidator()]);
      this.formArray.updateValueAndValidity();
    }
  }

  minimumPartnersValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const fa = control as FormArray;
      if (fa.length < KYB_MIN_PARTNERS) {
        return {
          minimumPartners: {
            required: KYB_MIN_PARTNERS,
            actual: fa.length,
          }
        };
      }
      return null;
    };
  }

  formArrayToPartnerValue(): (IndividualPartner | EntityPartner)[] {
    return (this.formArray.controls as FormGroup[]).map((fg: FormGroup) => {
      const v = fg.value;
      if (v.type === 'Individual' && !this.kybMode) {
        const individual = fromIndividualFormValue(v.individual);
        return {
          kind: 'Partner',
          type: 'Individual',
          ...(individual ?? {}),
          personalAssets: v.personalAssets ?? null,
          personalLiabilities: v.personalLiabilities ?? null,
        } as IndividualPartner;
      }
      // kyb individuals (flat name + dob controls) and entity partners keep the raw group value
      return v as IndividualPartner | EntityPartner;
    });
  }

  ngAfterViewInit() {
    setTimeout(()=> {
    });
  }


  doWriteValue(v: PartnerValue | null | undefined): void | PartnerValue {
    this.seeding = true;
    try {
      if (v) {
        for (const o of v) {
          this.addPartner(o);
        }
      } else { // if not given, add 2 by default
        this.addPartner();
        this.addPartner();
      }
    } finally {
      this.seeding = false;
    }
    this.emitEvent();
    return undefined;
  }

  private emitEvent() {
    if (this.seeding) {
      return;
    }
    this.events.emit({valid: this.formArray.valid, entries: this.formArray.value});
  }

  partnerFormGroups(): FormGroup[] {
    return this.formArray.controls as FormGroup[];
  }

  partnerFormGroupFormControl(memberFormGroup: FormGroup, controlName: string) {
    return memberFormGroup.controls[controlName] as FormControl;
  }

  // A partnership needs at least 2 partners, so removal is only offered above that floor.
  canRemovePartner(): boolean {
    return this.formArray.length > KYB_MIN_PARTNERS;
  }

  toggleSigners(partnerFormGroup: FormGroup): void {
    if (this.signersVisible.has(partnerFormGroup)) {
      this.signersVisible.delete(partnerFormGroup);
    } else {
      this.signersVisible.add(partnerFormGroup);
    }
  }

  isSignersVisible(partnerFormGroup: FormGroup): boolean {
    return this.signersVisible.has(partnerFormGroup);
  }

  signerEntity(partnerFormGroup: FormGroup): KybSignerEntityView | null {
    return this.signerEntityFor ? this.signerEntityFor(partnerFormGroup) : null;
  }

  startManualKyb(view: KybSignerEntityView): void {
    this.manualKybEntry.emit(view.entity.commercialEntityId);
  }

  showManualWaitingMessage(view: KybSignerEntityView): boolean {
    return kybDisplayStatus(view.entity.status) !== 'resolved';
  }

  // Names the waiting panel from the row itself, for the window before the row has an entity whose
  // legal name could be read instead.
  signerBusinessName(partnerFormGroup: FormGroup): string | null {
    return partnerFormGroup.get('manualEntry')?.value
      ? partnerFormGroup.get('businessName')?.value ?? null
      : partnerFormGroup.get('organisation')?.value?.name ?? null;
  }

  // Neither a trust nor a partnership has directors or shareholders for FrankieOne to return, so
  // such a partner gets no signers table; its signers come from the trustees/partners collected in
  // the nested section instead. The type is the one the row's ABN/ACN lookup returned.
  hasNestedParties(partnerFormGroup: FormGroup): boolean {
    const type = (partnerFormGroup.get('organisationType')?.value as EntityTypeValue | null)?.type;
    return type === 'trust' || type === 'partnership';
  }


  onPersonalAssetEvent($event: PersonalAssetComponentEvent, partnerFormGroup: FormGroup) {
    if ($event.entries && $event.entries.length) { // there are assets entries
      const formControl = this.partnerFormGroupFormControl(partnerFormGroup, 'personalAssets');
      formControl.setValidators([Validators.required]);
      formControl.updateValueAndValidity()
    } else {
      const formControl = this.partnerFormGroupFormControl(partnerFormGroup, 'personalAssets');
      formControl.clearValidators();
      formControl.updateValueAndValidity()
    }
  }

  onPersonalLiabilitiesEvent($event: PersonalLiabilitiesComponentEvent, partnerFormGroup: FormGroup) {
    if ($event.entries && $event.entries.length) {
      const formControl = this.partnerFormGroupFormControl(partnerFormGroup, 'personalLiabilities');
      formControl.setValidators([Validators.required]);
      formControl.updateValueAndValidity()
    } else {
      const formControl = this.partnerFormGroupFormControl(partnerFormGroup, 'personalLiabilities');
      formControl.clearValidators();
      formControl.updateValueAndValidity()
    }
  }


  removePartner(memberFormGroup: FormGroup) {
    const index = this.formArray.controls.indexOf(memberFormGroup);
    if (index !== -1) {
      this.formArray.removeAt(index);
    }
    this.signersVisible.delete(memberFormGroup);
    this.emitEvent();
  }

  addPartner(g?: Exclude<PartnerValue, null>[number]) {
    const formControlKind = this.formBuilder.control('Partner');
    // Partnership KYB defaults new partners to Individual; the standard partner flow defaults to Entity.
    const defaultType: IndividualOrEntity = this.kybMode ? 'Individual' : 'Entity';
    const formControlIndividualOrEntity = this.formBuilder.control<IndividualOrEntity>(defaultType, [Validators.required]);
    const formControlPersonalAssets = this.formBuilder.control(null);
    const formControlPersonalLiabilities = this.formBuilder.control(null);
    const formGroup = this.formBuilder.group({
      kind: formControlKind,
      type: formControlIndividualOrEntity,
      personalAssets: formControlPersonalAssets,
      personalLiabilities: formControlPersonalLiabilities,
    });
    if (g) {
      switch(g.type) {
        case 'Individual': {
          (formGroup.controls as any).type.setValue('Individual');
          this.switchToIndividualPartner(formGroup, g);
          break;
        }
        case 'Entity': {
          (formGroup.controls as any).type.setValue('Entity');
          this.switchToEntityPartner(formGroup, g);
          break;
        }
      }
    } else if (this.kybMode) {
      this.switchToIndividualPartner(formGroup);
    } else {
      this.switchToEntityPartner(formGroup, g);
    }
    this.formArray.push(formGroup);

    const subscription = formControlIndividualOrEntity.valueChanges.pipe(
      delay(0),
      tap((r: IndividualOrEntity|null) => {
        switch (r) {
          case 'Individual': {
            this.switchToIndividualPartner(formGroup);
            if (this.kybMode) {
              this.openKybIndividualDialog(formGroup);
            }
            break;
          }
          case 'Entity': {
            this.switchToEntityPartner(formGroup);
            break;
          }
        }
      })
    ).subscribe();
    this.subscriptions.push(subscription);
    this.emitEvent();
  }


  private switchToIndividualPartner(formGroup: FormGroup, partner?: IndividualPartner) {
    formGroup.removeControl('organisation');
    formGroup.removeControl('organisationType');
    formGroup.removeControl('acn');
    formGroup.removeControl('abn');

    if (this.kybMode) {
      // Only name + dob are captured on the KYB Key People screen; the remaining detail
      // is collected on the dedicated party detail page later in the flow.
      const formControlFirstName = this.formBuilder.control(partner ? partner.firstName : null, [Validators.required]);
      const formControlLastName = this.formBuilder.control(partner ? partner.lastName : null, [Validators.required]);
      const formControlMiddleName = this.formBuilder.control(partner ? partner.middleName : null);
      const formControlDob = this.formBuilder.control(partner ? partner.dob : null, [Validators.required]);

      formGroup.removeControl('individual');
      formGroup.removeControl('personalAssets');
      formGroup.removeControl('personalLiabilities');

      formGroup.addControl('firstName', formControlFirstName);
      formGroup.addControl('lastName', formControlLastName);
      formGroup.addControl('middleName', formControlMiddleName);
      formGroup.addControl('dob', formControlDob);
    } else {
      const formControlIndividual = this.formBuilder.control<IndividualFormValue>(toIndividualFormValue(partner), [Validators.required]);
      const formControlPersonalAssets = this.formBuilder.control(partner ? partner.personalAssets : null);
      const formControlPersonalLiabilities = this.formBuilder.control(partner ? partner.personalLiabilities : null);

      formGroup.removeControl('firstName');
      formGroup.removeControl('lastName');
      formGroup.removeControl('middleName');
      formGroup.removeControl('dob');

      formGroup.addControl('individual', formControlIndividual);
      formGroup.setControl('personalAssets', formControlPersonalAssets);
      formGroup.setControl('personalLiabilities', formControlPersonalLiabilities);
    }
  }

  // KYB only: add a new partner defaulted to Individual, leaving the Entity toggle available before
  // the user opens the name + dob popup via "Enter details".
  addKybIndividual(): void {
    this.addPartner();
  }

  // KYB only: popup to capture name + dob for an individual partner, then show it in the KYB table.
  openKybIndividualDialog(partnerFormGroup: FormGroup): void {
    const data: KybIndividualTrusteeDialogData = {
      title: 'Individual partner details',
      firstName: partnerFormGroup.get('firstName')?.value ?? null,
      middleName: partnerFormGroup.get('middleName')?.value ?? null,
      lastName: partnerFormGroup.get('lastName')?.value ?? null,
      dob: partnerFormGroup.get('dob')?.value ?? null,
    };
    this.subscriptions.push(
      this.dialogService.openKybIndividualTrusteeDialog(data).afterClosed().pipe(
        tap(result => {
          if (result) {
            partnerFormGroup.get('firstName')?.setValue(result.firstName);
            partnerFormGroup.get('middleName')?.setValue(result.middleName);
            partnerFormGroup.get('lastName')?.setValue(result.lastName);
            partnerFormGroup.get('dob')?.setValue(result.dob);
            partnerFormGroup.markAllAsTouched();
          }
        }),
      ).subscribe(),
    );
  }

  isKybIndividualComplete(partnerFormGroup: FormGroup): boolean {
    return !!partnerFormGroup.get('firstName')?.value
      && !!partnerFormGroup.get('lastName')?.value
      && !!partnerFormGroup.get('dob')?.value;
  }

  kybIndividualName(partnerFormGroup: FormGroup): string {
    return [partnerFormGroup.get('firstName')?.value, partnerFormGroup.get('middleName')?.value, partnerFormGroup.get('lastName')?.value]
      .map(v => (v ?? '').toString().trim())
      .filter(v => !!v)
      .join(' ');
  }

  private switchToEntityPartner(formGroup: FormGroup, partner?: EntityPartner) {
    const formControlOrganisationType = this.formBuilder.control(partner ? partner.organisationType : { type: 'company'}, [Validators.required]); // entity-type-selection
    const formControlOrganisation = this.formBuilder.control(partner ? partner.organisation : null, [Validators.required]); // business-search
    const formControlAcn = this.formBuilder.control(partner ? partner.acn : null);
    const formControlAbn = this.formBuilder.control(partner ? partner.abn : null);

    // formGroup.controls['type'].setValue('Entity');

    formGroup.removeControl('individual');
    formGroup.removeControl('firstName');
    formGroup.removeControl('lastName');
    formGroup.removeControl('middleName');
    formGroup.removeControl('dob');
    formGroup.removeControl('personalAssets');
    formGroup.removeControl('personalLiabilities');

    formGroup.addControl('organisation', formControlOrganisation);
    formGroup.addControl('organisationType', formControlOrganisationType);
    formGroup.addControl('acn', formControlAcn);
    formGroup.addControl('abn', formControlAbn);

    this.subscriptions.push(formControlOrganisation.valueChanges.pipe(
      tap((r: BusinessNumberSearchValue) => {
        if (r) {
          const abn = r.abn;
          let acn = r.acn;
          if (acn) {
            // add leading zeros if less than 9 digits
            acn = 9 - acn.length > 0 ? '0'.repeat(9 - acn.length) + acn : acn;
            formControlAcn.setValue(acn);
          } else {
            formControlAcn.setValue('');
          }
          if (abn) {
            formControlAbn.setValue(abn);
          } else {
            formControlAbn.setValue('');
          }
          if (r.result) {
            const entityType = getEntityType(r.result)
            formControlOrganisationType.setValue(entityType)
            if (entityType?.type == 'other' || !entityType) {
              this.entityTypeReadonly = false
            } else {
              this.entityTypeReadonly = true
            }
          } else {
            this.entityTypeReadonly = !!formControlOrganisationType.value
          }
        }
      })
    ).subscribe());
  }

  mark() {
    this.formGroup.markAllAsTouched();
    this.markObservable.next(true);
  }
}
